/**
 * Where the session's credentials live, and how they are renewed.
 *
 * Finding 3.27. Both tokens used to sit in ordinary cookies the page had
 * written to itself, so any script that ran on the page -- injected through one
 * of the `v-html` holes SMN-65/66 closed, or through a dependency -- could read
 * them with one line and use them from anywhere. The refresh token was the worse
 * of the two: it is valid for a month, and it mints access tokens on demand.
 *
 * Neither is reachable from JavaScript now.
 *
 * The **refresh token** is set by the server as an HttpOnly, Secure,
 * SameSite=strict cookie scoped to `/api/v1/authentication`. Nothing here reads
 * it, and nothing here can: the browser attaches it to the renewal request and
 * `document.cookie` never sees it. That is a backend change, because only a
 * server can set HttpOnly.
 *
 * The **access token** lives in the module variable below -- in memory, for the
 * lifetime of the page. It is gone on reload, which is why `restoreSession`
 * exists.
 *
 * The cost of that pairing is one request per page load, and the honest reason
 * for paying it is that memory is the only place a browser offers that script
 * cannot enumerate. localStorage and a readable cookie are both one line away
 * for anything running on the page; the finding is that the credentials were
 * somewhere that line reaches.
 */
import axios from 'axios'
import { VueCookieNext } from 'vue-cookie-next'
import { env } from '@/config/runtime-env'

/**
 * The readable half of the server's CSRF double submit pair.
 *
 * Deliberately readable -- that is the whole mechanism. The renewal request has
 * to echo this value in `X-CSRF-Token`, and only script on our own origin can
 * read a cookie of ours to echo it. A cross-site page can make the browser
 * *send* the HttpOnly refresh cookie, but it cannot read this one, so it cannot
 * produce a matching header and the server refuses the renewal.
 *
 * Named to match `authjwt_refresh_csrf_cookie_key` in the backend's config.
 */
export const CSRF_COOKIE = 'csrf_refresh_token'

/**
 * Cookies the portal wrote before this change.
 *
 * Read nowhere now, and cleared whenever the session is, because a browser that
 * signed in before this deploys is still carrying a working access token and a
 * month-long refresh token in exactly the place the finding is about.
 */
const LEGACY_TOKEN_COOKIES = ['route_token', 'route_refresh_token']

/** The access token, for as long as this page is open. */
let accessToken: string | null = null

export const readAccessToken = (): string | null => accessToken

export function storeAccessToken(token: string): void {
  accessToken = token
}

export function clearTokens(): void {
  accessToken = null
  restoreAttempt = null
  LEGACY_TOKEN_COOKIES.forEach(name => VueCookieNext.removeCookie(name))
}

/**
 * The renewal currently in flight, if any.
 *
 * A page rarely makes one request at a time. When an access token expires,
 * every request in flight fails at once, and without this each would start its
 * own renewal -- several concurrent refreshes, of which all but one are wasted.
 * They share one.
 */
let inFlight: Promise<string | null> | null = null

export function refreshAccessToken(): Promise<string | null> {
  if (!inFlight) {
    inFlight = renew().finally(() => {
      inFlight = null
    })
  }

  return inFlight
}

/**
 * The one attempt this page makes to pick up an existing session.
 *
 * Memoised rather than repeated: the router asks who is signed in before every
 * navigation, and without this a signed-out visitor clicking around the login
 * screen would fire a renewal request per click.
 *
 * Reset by `clearTokens`, which matters more than it looks: after a logout the
 * memoised promise still holds the token it resolved to, so returning it would
 * report the just-ended session as live.
 */
let restoreAttempt: Promise<string | null> | null = null

export function restoreSession(): Promise<string | null> {
  if (!restoreAttempt) restoreAttempt = refreshAccessToken()

  return restoreAttempt
}

/** Reset between tests; not used by the application. */
export function __resetRefreshState(): void {
  inFlight = null
  restoreAttempt = null
  accessToken = null
}

async function renew(): Promise<string | null> {
  try {
    const csrf = VueCookieNext.getCookie(CSRF_COOKIE)

    // A bare axios call, not the configured `client`. The client's request
    // interceptor would attach the *access* token -- the expired one that got
    // us here -- and its response interceptor would try to refresh the refresh
    // call, which is how a retry loop starts.
    //
    // The address is the renewal path alone, with the API base given as the base
    // address. axios.config.ts has already made that base the default of every
    // request, so writing the base into the address as well put it in twice
    // ('/api/v1/api/v1/authentication/refresh') whenever the base is relative --
    // a 404 that read as "no session" and signed the user out on every reload.
    //
    // `withCredentials` is what makes the browser attach the HttpOnly refresh
    // cookie. It is required when the portal and the API are not the same
    // origin and harmless when they are.
    const { data } = await axios.post(
      '/authentication/refresh',
      {},
      {
        baseURL: env.VUE_APP_API_BASE,
        withCredentials: true,
        headers: csrf ? { 'X-CSRF-Token': csrf } : {}
      }
    )

    // Success is "a token came back", not a status code. A refusal from the
    // business layer -- expired session, locked account, deactivated user --
    // is rendered with HTTP 200 and the reason buried in `info.details`, while
    // a rejected refresh token arrives as 422 in a different envelope shape
    // again. The one thing that means renewed is a token, so that is what is
    // checked.
    const token = data?.data?.access_token
    if (typeof token !== 'string' || token.length === 0) return null

    storeAccessToken(token)
    return token
  } catch (error) {
    // Network failure, a rejected refresh token, no cookie at all because
    // nobody has signed in on this browser. There is no difference worth making
    // here: the caller cannot renew, and the only honest next step is the login
    // screen.
    return null
  }
}
